import type { Locale } from '@xdocs/i18n';

/**
 * STOP-GAP local catalog for the `style:` scope (F65), following the exact
 * `documents:`/`reader:` precedent — NOT a second i18n runtime (E18/§13.3
 * ban), just physically scoped here because `packages/i18n/**` is outside
 * this task's ownership boundary.
 *
 * OPEN ISSUE: fold into `packages/i18n/src/catalogs/{en,ro}/style.json`.
 */
const en = {
  'back': 'Back to editor',
  'eyebrow': 'Styling',
  'title': 'Document themes',
  'description': 'A theme restyles your deliverables — fonts, palette, layout, cover. It’s a render job; your text is never regenerated.',
  'badge.pro': 'PRO',
  'badge.current': '● current',
  'theme.manuscript.name': 'Manuscript',
  'theme.manuscript.intended': 'Classic literary · books & fiction',
  'theme.academic.name': 'Academic',
  'theme.academic.intended': 'Textbooks, papers · wide margins',
  'theme.modern.name': 'Modern',
  'theme.modern.intended': 'Business & guides · high contrast',
  'theme.storybook.name': 'Storybook',
  'theme.storybook.intended': 'Children’s · illustration-forward',
  'theme.technical.name': 'Technical',
  'theme.technical.intended': 'Manuals, dev docs · grid + callouts',
  'theme.editorial.name': 'Editorial',
  'theme.editorial.intended': 'Longform, blog→book · pull-quotes',
  'theme.minimal.name': 'Minimal',
  'theme.minimal.intended': 'Essays, notes · ultra-clean',
  'theme.noir.name': 'Noir',
  'theme.noir.intended': 'Moody fiction · dark by default',
  'preview.title': 'Live preview · {themeName}',
  'preview.chapterOne': 'Chapter One',
  'preview.intendedFor': 'Intended for: {intended}',
  'preview.apply': 'Apply & re-render',
  'preview.applying': 'Applying…',
  'preview.applied': 'Applied',
  'preview.empty.title': 'Pick a Theme to preview it here',
  'toast.applied.action': 'Applied theme to',
  'toast.applyFailed': "Couldn't apply this theme — try again.",
  'upsell.description': '{themeName} is a PRO theme — upgrade to apply it to this document.',
  'upsell.upgrade': 'Upgrade to PRO',
  'error.title': "Couldn't load this document.",
  'error.retry': 'Retry',
  'notFound.title': "We couldn't find this document.",
  'notFound.cta': 'Back to documents',
  'forbidden.title': "You don't have access to style this document.",
  'forbidden.cta': 'Back to documents',
  'job.title': 'Re-rendering deliverables…',
} as const;

const ro: Record<keyof typeof en, string> = {
  'back': 'Înapoi la editor',
  'eyebrow': 'Stilizare',
  'title': 'Teme document',
  'description': 'O temă restilizează livrabilele tale — fonturi, paletă, aspect, copertă. Este o sarcină de randare; textul tău nu este niciodată regenerat.',
  'badge.pro': 'PRO',
  'badge.current': '● curentă',
  'theme.manuscript.name': 'Manuscris',
  'theme.manuscript.intended': 'Literar clasic · cărți & ficțiune',
  'theme.academic.name': 'Academic',
  'theme.academic.intended': 'Manuale, lucrări · margini largi',
  'theme.modern.name': 'Modern',
  'theme.modern.intended': 'Business & ghiduri · contrast ridicat',
  'theme.storybook.name': 'Poveste ilustrată',
  'theme.storybook.intended': 'Pentru copii · centrat pe ilustrații',
  'theme.technical.name': 'Tehnic',
  'theme.technical.intended': 'Manuale, documentație dev · grilă + note',
  'theme.editorial.name': 'Editorial',
  'theme.editorial.intended': 'Text lung, blog→carte · citate evidențiate',
  'theme.minimal.name': 'Minimal',
  'theme.minimal.intended': 'Eseuri, notițe · ultra-curat',
  'theme.noir.name': 'Noir',
  'theme.noir.intended': 'Ficțiune sumbră · întunecat implicit',
  'preview.title': 'Previzualizare live · {themeName}',
  'preview.chapterOne': 'Capitolul unu',
  'preview.intendedFor': 'Potrivit pentru: {intended}',
  'preview.apply': 'Aplică și randează din nou',
  'preview.applying': 'Se aplică…',
  'preview.applied': 'Aplicată',
  'preview.empty.title': 'Alege o temă pentru a o previzualiza aici',
  'toast.applied.action': 'Temă aplicată pentru',
  'toast.applyFailed': 'Nu am putut aplica această temă — încearcă din nou.',
  'upsell.description': '{themeName} este o temă PRO — treci la PRO pentru a o aplica acestui document.',
  'upsell.upgrade': 'Treci la PRO',
  'error.title': 'Nu am putut încărca acest document.',
  'error.retry': 'Reîncearcă',
  'notFound.title': 'Nu am găsit acest document.',
  'notFound.cta': 'Înapoi la documente',
  'forbidden.title': 'Nu ai acces să stilizezi acest document.',
  'forbidden.cta': 'Înapoi la documente',
  'job.title': 'Se randează livrabilele din nou…',
};

export const STYLE_CATALOG: Record<Locale, Record<keyof typeof en, string>> = { en, ro };

export type StyleCatalogKey = keyof typeof en;
